import { useState } from "react";
import { NavLink } from "react-router-dom";
import Eyebrow from "../../../shared/design/Eyebrow";
import Reveal from "../../../shared/design/Reveal";
import HeroImg from "../../../assets/home-aboutus-1.jpg";
import CtaImg from "../../../assets/bg-ropeswing.png";

// No careers backend exists — roles are static marketing copy ported from the
// Careers.dc.html mockup (todo #13). Department filtering is client-side only.
type Role = {
  title: string;
  dept: "Guiding" | "Operations" | "Marketing";
  loc: string;
  type: string;
  desc: string;
};

const ROLES: Role[] = [
  {
    title: "Lead Mountain Guide",
    dept: "Guiding",
    loc: "Banff, AB",
    type: "Full-time",
    desc: "Lead multi-day alpine expeditions across the Canadian Rockies. ACMG certification required.",
  },
  {
    title: "Desert Trip Guide",
    dept: "Guiding",
    loc: "Moab, UT",
    type: "Seasonal",
    desc: "Run our red-rock and canyon-country routes through the spring and fall seasons.",
  },
  {
    title: "Trip Designer",
    dept: "Operations",
    loc: "Remote",
    type: "Full-time",
    desc: "Build and cost out new expedition itineraries from scout notes to booking-ready trips.",
  },
  {
    title: "Guest Experience Lead",
    dept: "Operations",
    loc: "Jersey City, NJ",
    type: "Full-time",
    desc: "Own the traveler journey from first inquiry to post-trip — our voice before and after the trail.",
  },
  {
    title: "Content & Story Producer",
    dept: "Marketing",
    loc: "Remote",
    type: "Full-time",
    desc: "Capture and tell the Wander story in film, photo, and the field journal.",
  },
  {
    title: "Partnerships Manager",
    dept: "Marketing",
    loc: "Remote",
    type: "Full-time",
    desc: "Grow relationships with parks, outfitters, and conservation partners across the continent.",
  },
  {
    title: "Logistics Coordinator",
    dept: "Operations",
    loc: "Calgary, AB",
    type: "Full-time",
    desc: "Keep gear, permits, transport, and provisions moving for trips in the field.",
  },
];

const DEPTS = ["All", "Guiding", "Operations", "Marketing"] as const;
type Dept = (typeof DEPTS)[number];

const PERKS = [
  {
    icon: "🏔️",
    title: "Field days, paid",
    body: "Two weeks a year to go scout, climb, or just get lost on us.",
  },
  {
    icon: "🩺",
    title: "Full health cover",
    body: "Medical, dental, and vision from day one, plus wilderness-med training.",
  },
  {
    icon: "🌲",
    title: "Remote-first",
    body: "Work from a trailhead, a cabin, or wherever the wifi reaches.",
  },
  {
    icon: "🎒",
    title: "Free expeditions",
    body: "One Wander trip a year, on the house, for you and a plus-one.",
  },
];

const Careers = () => {
  const [dept, setDept] = useState<Dept>("All");
  const roles = dept === "All" ? ROLES : ROLES.filter((r) => r.dept === dept);

  const scrollToRoles = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.getElementById("roles")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-sand">
      {/* HERO */}
      <section className="px-6 pt-24 md:px-12 md:pt-28">
        <div className="relative mx-auto max-w-[1320px] overflow-hidden rounded-[28px] shadow-[0_40px_80px_-30px_rgba(19,58,69,0.5)]">
          <img
            src={HeroImg}
            alt="Wander travelers gathered on a beach at sunset"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(19,58,69,0.92),rgba(19,58,69,0.5))]" />
          <div className="relative px-8 py-20 text-white md:px-14 md:py-28">
            <Eyebrow tone="lime" className="mb-4">
              CAREERS AT WANDER
            </Eyebrow>
            <h1 className="mb-[18px] max-w-[760px] font-serif text-[40px] font-normal leading-[1.0] tracking-[-0.02em] md:text-[62px]">
              Work where the
              <br />
              map runs out.
            </h1>
            <p className="mb-[30px] max-w-[520px] text-[17px] leading-relaxed text-[#dde7e3] md:text-[18px]">
              We're a small team of guides, planners, and storytellers obsessed
              with showing people the wildest corners of North America. If
              that's your kind of office, read on.
            </p>
            <a
              href="#roles"
              onClick={scrollToRoles}
              className="inline-block rounded-full bg-primary-500 px-7 py-[15px] text-[15.5px] font-bold text-ink transition-colors hover:bg-primary-300"
            >
              See open roles ↓
            </a>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="mx-auto max-w-[1320px] px-6 pb-5 pt-14 md:px-12">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PERKS.map((perk, i) => (
            <Reveal key={perk.title} delay={(i % 4) * 0.05}>
              <div className="h-full rounded-2xl bg-white p-6">
                <div className="mb-3 text-[24px]">{perk.icon}</div>
                <div className="mb-1.5 text-[16px] font-bold text-ink">
                  {perk.title}
                </div>
                <div className="text-[14px] leading-relaxed text-muted-400">
                  {perk.body}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OPEN ROLES */}
      <section
        id="roles"
        className="mx-auto max-w-[1320px] scroll-mt-24 px-6 pb-5 pt-12 md:px-12"
      >
        <div className="mb-3.5 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow className="mb-2.5">NOW HIRING</Eyebrow>
            <h2 className="font-serif text-[32px] font-normal tracking-[-0.02em] text-ink md:text-[42px]">
              Open positions
            </h2>
          </div>
          <div className="flex flex-wrap gap-[7px]">
            {DEPTS.map((d) => {
              const active = dept === d;
              return (
                <button
                  key={d}
                  onClick={() => setDept(d)}
                  className={`rounded-full px-4 py-[9px] text-[13.5px] font-semibold transition-colors ${
                    active
                      ? "border border-secondary-400 bg-secondary-400 text-white"
                      : "border border-line bg-white text-muted hover:border-secondary-400"
                  }`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3.5">
          {roles.map((role) => (
            <Reveal key={role.title}>
              <NavLink
                to="/contact"
                className="group flex items-center gap-5 rounded-2xl bg-white p-[22px] shadow-[0_12px_26px_-22px_rgba(0,0,0,0.3)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_44px_-26px_rgba(0,0,0,0.4)]"
              >
                <div className="min-w-0 flex-1">
                  <div className="mb-1.5 flex flex-wrap items-center gap-3">
                    <span className="font-serif text-[22px] text-ink">
                      {role.title}
                    </span>
                    <span className="rounded-full bg-secondary-400/10 px-[11px] py-1 text-[11.5px] font-bold text-secondary-400">
                      {role.dept}
                    </span>
                  </div>
                  <p className="mb-2 max-w-[620px] text-[14px] leading-relaxed text-muted-400">
                    {role.desc}
                  </p>
                  <div className="flex gap-[18px] font-mono text-[13px] text-muted">
                    <span>⌖ {role.loc}</span>
                    <span>◷ {role.type}</span>
                  </div>
                </div>
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full border border-line text-[18px] text-ink transition-all duration-300 group-hover:translate-x-[3px] group-hover:border-secondary-400 group-hover:bg-secondary-400 group-hover:text-white">
                  →
                </span>
              </NavLink>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="mx-auto mt-12 max-w-[1320px] px-6 pb-10 md:px-12">
        <Reveal>
          <div className="relative overflow-hidden rounded-[28px] px-8 py-16 text-center text-white md:px-12">
            <img
              src={CtaImg}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(19,58,69,0.92),rgba(19,58,69,0.55))]" />
            <div className="relative mx-auto max-w-[600px]">
              <h2 className="mb-3.5 font-serif text-[32px] font-normal leading-[1.06] md:text-[42px]">
                Don't see your role?
              </h2>
              <p className="mb-[30px] text-[16px] leading-relaxed text-[#dde7e3] md:text-[17px]">
                We're always glad to meet good people who love their corner of
                the continent. Tell us where you call wild and what you'd bring
                to the team.
              </p>
              <NavLink
                to="/contact"
                className="inline-flex items-center gap-2.5 rounded-full bg-primary-500 px-[30px] py-4 text-[16px] font-bold text-ink transition-colors hover:bg-primary-300"
              >
                Contact us <span className="text-[18px]">→</span>
              </NavLink>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Careers;
